import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import AuthService from "../../services/authService";

import { setUserData, clearAuthState } from "./userSlice";

/* =========================================================
   LOGIN
   ========================================================= */

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (credentials, thunkAPI) => {
    try {
      const payload = await AuthService.login(credentials);

      // IMPORTANT:
      // Do NOT store token/refreshToken.
      // They are HttpOnly cookies.
      thunkAPI.dispatch(
        setUserData({
          user: payload,
          role: payload?.roles?.[0]?.roleName || null,
          isAuthenticated: true,
        }),
      );

      return payload;
    } catch (error) {
      console.error("Login error:", error);

      const msg = error?.response?.data?.message;

      return thunkAPI.rejectWithValue(
        typeof msg === "string" ? msg : msg?.message || "Login failed",
      );
    }
  },
);

/* =========================================================
   LOGOUT
   ========================================================= */
export const logoutUser = createAsyncThunk(
  "auth/logoutUser",

  async (_, thunkAPI) => {
    try {
      await AuthService.logout();
    } catch (error) {
      /*
       * Even if logout API fails, clear Redux state.
       */
      console.warn("Logout API failed:", error);
    }

    // Clear user authentication state
    thunkAPI.dispatch(clearAuthState());

    return true;
  },
);

/* =========================================================
   FORGOT PASSWORD
   ========================================================= */

export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",

  async (data, thunkAPI) => {
    try {
      const res = await AuthService.forgotPassword(data);

      return res.data?.data;
    } catch (error) {
      const msg = error?.response?.data?.message;

      const errorMessage =
        typeof msg === "string"
          ? msg
          : msg?.message || "Forgot password failed";

      return thunkAPI.rejectWithValue(errorMessage);
    }
  },
);

/* =========================================================
   RESET PASSWORD
   ========================================================= */

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",

  async (data, thunkAPI) => {
    try {
      const res = await AuthService.resetPassword(data);

      return res.data?.data;
    } catch (error) {
      const msg = error?.response?.data?.message;

      const errorMessage =
        typeof msg === "string" ? msg : msg?.message || "Reset password failed";

      return thunkAPI.rejectWithValue(errorMessage);
    }
  },
);

/* =========================================================
   CHANGE PASSWORD
   ========================================================= */

export const changePassword = createAsyncThunk(
  "auth/changePassword",

  async (data, thunkAPI) => {
    try {
      const res = await AuthService.changePassword(data);

      return res.data?.data;
    } catch (error) {
      const msg = error?.response?.data?.message || "Change password failed";

      return thunkAPI.rejectWithValue(msg);
    }
  },
);

/* =========================================================
   AUTH SLICE
   ========================================================= */

const initialState = {
  loading: false,
  error: null,
  success: null,
};

const authSlice = createSlice({
  name: "auth",

  initialState,

  reducers: {
    clearError(state) {
      state.error = null;
    },

    clearSuccess(state) {
      state.success = null;
    },
  },

  extraReducers: (builder) => {
    builder

      /* LOGIN */

      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(loginUser.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })

      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Login failed";
      })

      /* LOGOUT */

      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
      })

      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })

      /* FORGOT PASSWORD */

      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(forgotPassword.fulfilled, (state) => {
        state.loading = false;

        state.success = "Forgot Password link sent to your mail";
      })

      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;

        state.error = action.payload;
      })

      /* RESET PASSWORD */

      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;

        state.success = "Your password has been changed";
      })

      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;

        state.error = action.payload;
      })

      /* CHANGE PASSWORD */

      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false;

        state.success = "Your password has been changed";
      })

      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;

        state.error = action.payload;
      });
  },
});

export const { clearError, clearSuccess } = authSlice.actions;

export default authSlice.reducer;
